import { useState, useEffect, useMemo, useRef } from "react";
import { useLocation, useSearch } from "wouter";
import { useUser } from "@clerk/react";
import {
  useAssistantAnalyzeNow,
  useGetAssistantAnalyzeJob,
  usePauseAssistantAnalyzeJob,
  useResumeAssistantAnalyzeJob,
  useCancelAssistantAnalyzeJob,
  useListSuggestions,
  useAcceptSuggestion,
  useRejectSuggestion,
  useListMailAccounts,
  useListInbox,
  getGetAssistantAnalyzeJobQueryKey,
  getGetTodayQueryKey,
  getListSuggestionsQueryKey,
  getListProjectsQueryKey,
  getListInboxQueryKey,
  type EnrichedSuggestion,
  type InboxItem,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Loader2,
  Mail,
  Sparkles,
  CheckCircle2,
  ArrowRight,
  X,
  Calendar,
  Inbox as InboxIcon,
  ListChecks,
  AlertTriangle,
  Pause,
  Play,
  RotateCcw,
  XCircle,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import MailAccountForm from "@/components/mail-account-form";
import { useToast } from "@/hooks/use-toast";
import Logo from "@/components/logo";

// Accounts older than this are treated as returning users when they land on
// /onboarding without valid localStorage flags (new device, cleared storage…).
const ONBOARDING_WINDOW_MS = 30 * 60 * 1_000; // 30 minutes

function onboardingDoneKey(userId: string | null | undefined): string {
  return userId
    ? `onenext_onboarding_done:${userId}`
    : "onenext_onboarding_done";
}

function firstAnalysisDoneKey(userId: string | null | undefined): string {
  return userId
    ? `onenext_first_analysis_done:${userId}`
    : "onenext_first_analysis_done";
}

// Persisted onboarding step index. Lets a refresh (especially during the
// long-running analysis on step 2) resume right where the user was instead
// of dropping back to the welcome screen.
function onboardingStepKey(userId: string | null | undefined): string {
  return userId
    ? `onenext_onboarding_step:${userId}`
    : "onenext_onboarding_step";
}

function readPersistedStep(userId: string | null | undefined): number {
  if (typeof window === "undefined") return 0;
  const raw = window.localStorage.getItem(onboardingStepKey(userId));
  if (!raw) return 0;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 && n <= 1 ? n : 0;
}

function writePersistedStep(
  userId: string | null | undefined,
  step: number,
): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(onboardingStepKey(userId), String(step));
}

function StepIndicator({ current, total }: { current: number; total: number }) {
  return (
    <div className="flex items-center gap-2">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className={`h-1.5 rounded-full transition-all duration-300 ${
            i < current ? "bg-primary w-8" : i === current ? "bg-primary w-8" : "bg-border w-4"
          }`}
        />
      ))}
    </div>
  );
}

function Step1Promise({ onNext, onSkip }: { onNext: () => void; onSkip: () => void }) {
  const { t } = useTranslation();
  const steps = [
    { icon: <Mail className="w-5 h-5 text-primary" />, title: t("onboarding_page.step_connect_title"), desc: t("onboarding_page.step_connect_desc") },
    { icon: <Sparkles className="w-5 h-5 text-primary" />, title: t("onboarding_page.step_ai_title"), desc: t("onboarding_page.step_ai_desc") },
    { icon: <CheckCircle2 className="w-5 h-5 text-primary" />, title: t("onboarding_page.step_execute_title"), desc: t("onboarding_page.step_execute_desc") },
  ];
  return (
    <div className="flex flex-col items-center text-center space-y-8 max-w-lg mx-auto">
      <div className="w-20 h-20 flex items-center justify-center">
        <Logo size={72} />
      </div>

      <div className="space-y-3">
        <h1 className="text-4xl font-bold text-foreground tracking-tight">
          {t("onboarding_page.welcome_title")}
        </h1>
        <p className="text-lg text-muted-foreground leading-relaxed">
          {t("onboarding_page.welcome_desc")}
        </p>
      </div>

      <div className="w-full grid gap-3">
        {steps.map((item) => (
          <div
            key={item.title}
            className="flex items-start gap-4 bg-secondary/30 rounded-xl p-4 text-left border border-border/40"
          >
            <div className="mt-0.5 shrink-0">{item.icon}</div>
            <div>
              <p className="font-semibold text-sm">{item.title}</p>
              <p className="text-sm text-muted-foreground">{item.desc}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-col gap-3 w-full pt-2">
        <Button size="lg" className="w-full rounded-xl" onClick={onNext}>
          {t("onboarding_page.begin_btn")} <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
        <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={onSkip}>
          {t("onboarding_page.skip_intro_btn")}
        </Button>
      </div>
    </div>
  );
}

function Step2Mail({
  onNext,
  onSkip,
  onSkipMail,
}: {
  onNext: () => void;
  onSkip: () => void;
  onSkipMail: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="w-full max-w-2xl mx-auto space-y-6">
      <div className="text-center space-y-2">
        <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto">
          <Mail className="w-6 h-6 text-primary" />
        </div>
        <h2 className="text-2xl font-bold text-foreground">{t("onboarding_page.connect_title")}</h2>
        <p className="text-muted-foreground">
          {t("onboarding_page.connect_desc")}
        </p>
      </div>

      <div className="bg-card border border-border rounded-2xl p-6 shadow-sm">
        <MailAccountForm onSaved={onNext} onCancel={onSkip} />
      </div>

      <div className="text-center">
        <button
          type="button"
          onClick={onSkipMail}
          className="text-sm text-muted-foreground hover:text-foreground transition-colors underline-offset-4 hover:underline"
          data-testid="btn-skip-mail"
        >
          {t("onboarding_page.skip_mail_btn")}
        </button>
      </div>
    </div>
  );
}

type DateRange = {
  id: "30" | "90" | "all";
  label: string;
  sub: string;
  sinceDays: number | null;
  /**
   * Full-sync depth sent to the API so the IMAP history is pulled BEFORE
   * the analysis runs. Without this, the analysis only sees the small
   * working set kept warm by the 90s scheduler (~50 mails per folder).
   * Mapping: 30 jours → "3m" (closest available enum, the analysis still
   * filters by sinceDays=30); 90 jours → "12m"; toute la boîte → "all".
   */
  depth: "3m" | "12m" | "all";
};

const RANGES: DateRange[] = [
  { id: "30", label: "", sub: "", sinceDays: 30, depth: "3m" },
  { id: "90", label: "", sub: "", sinceDays: 90, depth: "12m" },
  { id: "all", label: "", sub: "", sinceDays: null, depth: "all" },
];

type MaxMailsPreset = {
  id: "100" | "500" | "1000" | "unlimited";
  label: string;
  /** `null` means no extra cap — only the daily AI quota and depth window apply. */
  value: number | null;
};

const MAX_MAILS_PRESETS: MaxMailsPreset[] = [
  { id: "100", label: "100", value: 100 },
  { id: "500", label: "500", value: 500 },
  { id: "1000", label: "1 000", value: 1000 },
  { id: "unlimited", label: "", value: null },
];

// LocalStorage helpers for analysis-job persistence. Survives page reload /
// network blip during the long IMAP-sync + LLM batch so the user lands back
// on the in-progress UI instead of restarting from scratch.
const ANALYSIS_JOB_STORAGE_KEY = "onenext.onboarding.analysisJobId";

function readPersistedAnalysisJob(userId?: string | null): string | null {
  if (typeof window === "undefined") return null;
  const raw = window.localStorage.getItem(ANALYSIS_JOB_STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { userId?: string; jobId?: string };
    if (!parsed.jobId) return null;
    if (userId && parsed.userId && parsed.userId !== userId) return null;
    return parsed.jobId;
  } catch {
    return null;
  }
}

function writePersistedAnalysisJob(userId: string | null | undefined, jobId: string | null) {
  if (typeof window === "undefined") return;
  if (!jobId) {
    window.localStorage.removeItem(ANALYSIS_JOB_STORAGE_KEY);
    return;
  }
  window.localStorage.setItem(
    ANALYSIS_JOB_STORAGE_KEY,
    JSON.stringify({ userId: userId ?? null, jobId }),
  );
}

function Step3Analysis({
  onDone,
  onSkip,
}: {
  onDone: () => void;
  onSkip: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const { user } = useUser();
  const [selected, setSelected] = useState<DateRange["id"]>("90");
  const [maxPreset, setMaxPreset] =
    useState<MaxMailsPreset["id"]>("500");
  const [jobId, setJobIdState] = useState<string | null>(() =>
    readPersistedAnalysisJob(user?.id),
  );
  const setJobId = (id: string | null) => {
    writePersistedAnalysisJob(user?.id, id);
    setJobIdState(id);
  };
  // jobOutcome captures the *terminal* state separately so we can render
  // distinct success vs. failure UIs (and never silently treat a failed
  // job as success that lets the user proceed). `cancelled` is treated
  // as a terminal-but-recoverable state — the user can hit "Recommencer"
  // to start a fresh job with the same parameters.
  type Outcome = "pending" | "done" | "failed" | "cancelled";
  const [jobOutcome, setJobOutcome] = useState<Outcome>("pending");
  const analyzeNow = useAssistantAnalyzeNow();
  const pauseJob = usePauseAssistantAnalyzeJob();
  const resumeJob = useResumeAssistantAnalyzeJob();
  const cancelJob = useCancelAssistantAnalyzeJob();

  const job = useGetAssistantAnalyzeJob(jobId ?? "", {
    query: {
      queryKey: getGetAssistantAnalyzeJobQueryKey(jobId ?? ""),
      enabled: !!jobId && jobOutcome === "pending",
      refetchInterval: (query) => {
        const data = query.state.data as { status?: string } | undefined;
        // Keep polling while paused too — the orchestrator still advances
        // counters for accounts that paused after partial download, and
        // the user expects the progress bar to stay live.
        return data?.status === "running" || data?.status === "paused"
          ? 1500
          : false;
      },
    },
  });

  useEffect(() => {
    if (!job.data) return;
    if (job.data.status === "done") {
      setJobOutcome("done");
      // Job reached terminal state — clear the persisted reference so the
      // next visit doesn't keep polling a finished job forever.
      writePersistedAnalysisJob(user?.id, null);
    } else if (job.data.status === "failed") {
      setJobOutcome("failed");
      writePersistedAnalysisJob(user?.id, null);
    } else if (job.data.status === "cancelled") {
      setJobOutcome("cancelled");
      writePersistedAnalysisJob(user?.id, null);
    }
  }, [job.data, user?.id]);

  const resetForRetry = () => {
    setJobId(null);
    setJobOutcome("pending");
  };

  const handlePause = () => {
    if (!jobId) return;
    pauseJob.mutate({ jobId });
  };
  const handleResume = () => {
    if (!jobId) return;
    resumeJob.mutate({ jobId });
  };
  const handleCancel = () => {
    if (!jobId) return;
    cancelJob.mutate(
      { jobId },
      {
        onSuccess: () => {
          toast({
            title: t("onboarding_page.analysis_cancelled_title"),
            description: t("onboarding_page.analysis_cancelled_cancel_desc"),
          });
        },
      },
    );
  };
  const handleRestart = () => {
    if (jobId) {
      // Fire-and-forget cancel: the backend is idempotent and the new
      // POST below will replace the per-user pointer regardless. We
      // still clear local state immediately so the UI flips out of the
      // "cancelled" view without waiting on the round-trip.
      cancelJob.mutate({ jobId });
    }
    resetForRetry();
    start();
  };

  const start = () => {
    const range = RANGES.find((r) => r.id === selected);
    if (!range) return;
    const preset =
      MAX_MAILS_PRESETS.find((p) => p.id === maxPreset) ??
      MAX_MAILS_PRESETS[1];
    setJobOutcome("pending");
    analyzeNow.mutate(
      {
        data: {
          sinceDays: range.sinceDays,
          unlimited: true,
          // Crucial: triggers a full IMAP sync first so the analysis sees
          // the entire mailbox up to the chosen depth, not just the small
          // working set kept warm by the 90-second scheduler.
          depth: range.depth,
          // Hard ceiling chosen by the user (100 / 500 / 1 000 / illimité).
          // Stops the analysis from committing to the entire backlog when
          // the mailbox is huge — gives them a "solid base" they can grow
          // by relaunching with a higher preset later.
          maxMails: preset.value,
        },
      },
      {
        onSuccess: (resp) => {
          setJobId(resp.jobId);
          // When depth is set, the API returns 0/0 in the immediate response
          // because totals are only known AFTER the IMAP sync completes.
          // Don't short-circuit to "done" in that case — let the polling
          // GET endpoint deliver real numbers once the analyzing phase begins.
          if (
            !range.depth &&
            resp.inboxTotal === 0 &&
            resp.mailsTotal === 0
          ) {
            toast({
              title: "Rien à analyser",
              description:
                "Aucun e-mail ou capture en attente — passons à la validation.",
            });
            setJobOutcome("done");
          }
        },
        onError: () =>
          toast({
            title: t("onboarding_page.analysis_error_title"),
            description: t("onboarding_page.analysis_error_desc"),
            variant: "destructive",
          }),
      },
    );
  };

  if (jobId) {
    const phase = job.data?.phase ?? "analyzing";
    const isSyncing = phase === "syncing" && jobOutcome === "pending";
    const syncTotal = job.data?.syncTotal ?? 0;
    const syncDone = job.data?.syncDone ?? 0;
    const syncFolder = job.data?.syncCurrentFolder ?? null;
    const syncMailsTotal = job.data?.syncMailsTotal ?? 0;
    const syncMailsDone = job.data?.syncMailsDone ?? 0;
    // Prefer per-mail progress (more granular & changes constantly during a
    // download) when the worker has had time to estimate the folder sizes.
    // Fall back to the per-account ratio at the very start of the sync,
    // before any folder has been probed.
    const syncPct =
      syncMailsTotal > 0
        ? Math.min(
            100,
            Math.round((syncMailsDone / syncMailsTotal) * 100),
          )
        : syncTotal > 0
          ? Math.round((syncDone / syncTotal) * 100)
          : 0;
    const total = (job.data?.inboxTotal ?? 0) + (job.data?.mailsTotal ?? 0);
    const done = (job.data?.inboxDone ?? 0) + (job.data?.mailsDone ?? 0);
    const failed = (job.data?.inboxFailed ?? 0) + (job.data?.mailsFailed ?? 0);
    const pct = total > 0 ? Math.round((done / total) * 100) : 0;
    const capReached = job.data?.capReached ?? false;
    const isDone = jobOutcome === "done";
    const isFailed = jobOutcome === "failed";
    const isCancelled = jobOutcome === "cancelled";
    // Reflect the orchestrator's reported status so pause/resume buttons
    // toggle as soon as the backend acknowledges the request, not only
    // when the next sync poll lands. `paused` is a non-terminal state.
    const liveStatus = job.data?.status;
    const isPaused = !isDone && !isFailed && !isCancelled && liveStatus === "paused";
    const isStopped = isFailed || isCancelled;
    // Disable controls while a control request is mid-flight to avoid
    // double-firing. Pause / resume / cancel are all idempotent on the
    // backend but disabling the buttons gives clearer UX feedback.
    const controlsBusy =
      pauseJob.isPending || resumeJob.isPending || cancelJob.isPending;

    return (
      <div className="flex flex-col items-center text-center space-y-6 max-w-lg mx-auto">
        <div
          className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
            isStopped
              ? "bg-destructive/10"
              : isPaused
                ? "bg-amber-100"
                : "bg-primary/10"
          }`}
        >
          {isFailed ? (
            <AlertTriangle className="w-6 h-6 text-destructive" />
          ) : isCancelled ? (
            <XCircle className="w-6 h-6 text-destructive" />
          ) : isDone ? (
            <CheckCircle2 className="w-6 h-6 text-primary" />
          ) : isPaused ? (
            <Pause className="w-6 h-6 text-amber-600" />
          ) : (
            <Loader2 className="w-6 h-6 text-primary animate-spin" />
          )}
        </div>
        <div className="space-y-2">
          <h2 className="text-2xl font-bold">
            {isFailed
              ? t("onboarding_page.analysis_failed_title")
              : isCancelled
                ? t("onboarding_page.analysis_cancelled_title")
                : isDone
                  ? t("onboarding_page.analysis_done_title")
                  : isPaused
                    ? t("onboarding_page.analysis_paused_title")
                    : isSyncing
                      ? t("onboarding_page.analysis_syncing_title")
                      : t("onboarding_page.analysis_running_title")}
          </h2>
          <p className="text-muted-foreground">
            {isFailed
              ? t("onboarding_page.analysis_failed_desc")
              : isCancelled
                ? t("onboarding_page.analysis_cancelled_desc")
                : isDone
                  ? t("onboarding_page.analysis_done_desc", { done, failed })
                  : isPaused
                    ? t("onboarding_page.analysis_paused_desc")
                    : isSyncing
                      ? t("onboarding_page.analysis_syncing_desc")
                      : t("onboarding_page.analysis_running_desc")}
          </p>
        </div>

        {!isStopped && isSyncing && (
          <div className="w-full space-y-2" data-testid="onboarding-sync-progress">
            <Progress value={syncPct} className="h-2" />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>
                {syncMailsTotal > 0
                  ? t("onboarding_page.sync_progress_of", { done: syncMailsDone.toLocaleString(i18n.language === "en" ? "en-GB" : "fr-FR"), total: syncMailsTotal.toLocaleString(i18n.language === "en" ? "en-GB" : "fr-FR") })
                  : syncMailsDone > 0
                    ? t("onboarding_page.sync_progress_count", { done: syncMailsDone.toLocaleString(i18n.language === "en" ? "en-GB" : "fr-FR") })
                    : t("onboarding_page.sync_preparing")}
              </span>
              <span>{syncPct}%</span>
            </div>
            <div className="flex justify-between text-xs text-muted-foreground">
              <span className="truncate">
                {syncFolder
                  ? t("onboarding_page.sync_folder", { folder: syncFolder })
                  : "\u00a0"}
              </span>
              <span className="shrink-0 ml-2">
                {t("onboarding_page.sync_accounts", { done: syncDone, total: syncTotal })}
              </span>
            </div>
          </div>
        )}

        {!isStopped && !isSyncing && (
          <div className="w-full space-y-2">
            <Progress value={pct} className="h-2" />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>
                {done} / {total}
              </span>
              <span>{pct}%</span>
            </div>
          </div>
        )}

        {isDone && <FirstAnalyzedMailPreview />}

        {capReached && !isStopped && (
          <div className="flex items-start gap-3 w-full bg-amber-50 border border-amber-200 rounded-xl p-4 text-left">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold text-amber-900">
                {t("onboarding_page.cap_reached_title")}
              </p>
              <p className="text-amber-800">
                {t("onboarding_page.cap_reached_desc")}
              </p>
            </div>
          </div>
        )}

        {isStopped ? (
          <div className="flex flex-col gap-2 w-full">
            <Button
              size="lg"
              className="w-full rounded-xl"
              onClick={handleRestart}
              disabled={analyzeNow.isPending}
              data-testid="button-onboarding-restart-analysis"
            >
              {analyzeNow.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RotateCcw className="w-4 h-4 mr-2" />
              )}
              {isCancelled ? t("onboarding_page.restart_analysis_btn") : t("onboarding_page.retry_btn")}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground"
              onClick={onSkip}
            >
              {t("onboarding_page.skip_step_btn")}
            </Button>
          </div>
        ) : isDone ? (
          <Button
            size="lg"
            className="w-full rounded-xl"
            onClick={onDone}
            data-testid="button-onboarding-see-suggestions"
          >
            {t("onboarding_page.see_suggestions_btn")} <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        ) : (
          // Live controls: pause / resume + cancel + (still primary)
          // "Patientez…" placeholder so the layout doesn't shift when
          // the analysis finishes. The placeholder is hidden once the
          // user can take an action.
          <div className="flex flex-col gap-2 w-full">
            <div className="grid grid-cols-2 gap-2 w-full">
              {isPaused ? (
                <Button
                  variant="secondary"
                  size="lg"
                  className="rounded-xl"
                  onClick={handleResume}
                  disabled={controlsBusy}
                  data-testid="button-onboarding-resume-analysis"
                >
                  {resumeJob.isPending ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Play className="w-4 h-4 mr-2" />
                  )}
                  {t("onboarding_page.resume_btn")}
                </Button>
              ) : (
                <Button
                  variant="secondary"
                  size="lg"
                  className="rounded-xl"
                  onClick={handlePause}
                  disabled={controlsBusy}
                  data-testid="button-onboarding-pause-analysis"
                >
                  {pauseJob.isPending ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Pause className="w-4 h-4 mr-2" />
                  )}
                  {t("onboarding_page.pause_btn")}
                </Button>
              )}
              <Button
                variant="outline"
                size="lg"
                className="rounded-xl"
                onClick={handleCancel}
                disabled={controlsBusy}
                data-testid="button-onboarding-cancel-analysis"
              >
                {cancelJob.isPending ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <XCircle className="w-4 h-4 mr-2" />
                )}
                {t("onboarding_page.cancel_btn")}
              </Button>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground"
              onClick={handleRestart}
              disabled={analyzeNow.isPending || controlsBusy}
              data-testid="button-onboarding-restart-running-analysis"
            >
              <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
              {t("onboarding_page.restart_from_scratch_btn")}
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center text-center space-y-6 max-w-lg mx-auto">
      <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center">
        <Sparkles className="w-6 h-6 text-primary" />
      </div>

      <div className="space-y-2">
        <h2 className="text-2xl font-bold text-foreground">{t("onboarding_page.first_analysis_title")}</h2>
        <p className="text-muted-foreground">
          {t("onboarding_page.first_analysis_desc")}
        </p>
      </div>

      <div className="w-full grid gap-3">
        {RANGES.map((r) => {
          const isSelected = selected === r.id;
          const label = t(`onboarding_page.range_${r.id}_label`);
          const sub = t(`onboarding_page.range_${r.id}_sub`);
          return (
            <button
              key={r.id}
              type="button"
              onClick={() => setSelected(r.id)}
              className={`flex items-start gap-4 rounded-xl p-4 text-left border transition-all ${
                isSelected
                  ? "bg-primary/5 border-primary shadow-sm"
                  : "bg-secondary/30 border-border/40 hover:border-border"
              }`}
            >
              <Calendar
                className={`w-5 h-5 mt-0.5 shrink-0 ${
                  isSelected ? "text-primary" : "text-muted-foreground"
                }`}
              />
              <div className="flex-1">
                <p className="font-semibold text-sm">{label}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>
              </div>
              {isSelected && (
                <CheckCircle2 className="w-5 h-5 text-primary shrink-0" />
              )}
            </button>
          );
        })}
      </div>

      <div className="w-full text-left space-y-2">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          {t("onboarding_page.max_mails_label")}
        </p>
        <div
          className="grid grid-cols-4 gap-2"
          data-testid="onboarding-max-mails"
        >
          {MAX_MAILS_PRESETS.map((p) => {
            const isOn = maxPreset === p.id;
            const label = p.id === "unlimited" ? t("onboarding_page.limit_unlimited") : p.label;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => setMaxPreset(p.id)}
                className={`rounded-lg border px-2 py-2 text-sm font-medium transition-all ${
                  isOn
                    ? "bg-primary/5 border-primary text-primary shadow-sm"
                    : "bg-secondary/30 border-border/40 text-foreground hover:border-border"
                }`}
                data-testid={`onboarding-max-mails-${p.id}`}
              >
                {label}
              </button>
            );
          })}
        </div>
        <p className="text-xs text-muted-foreground">
          {t("onboarding_page.max_mails_hint")}
        </p>
      </div>

      <div className="flex flex-col gap-2 w-full pt-2">
        <Button
          size="lg"
          className="w-full rounded-xl"
          onClick={start}
          disabled={analyzeNow.isPending}
        >
          {analyzeNow.isPending && (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          )}
          {t("onboarding_page.start_analysis_btn")} <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="text-muted-foreground"
          onClick={onSkip}
          disabled={analyzeNow.isPending}
        >
          {t("onboarding_page.skip_step_btn")}
        </Button>
      </div>
    </div>
  );
}

const TYPE_COLORS: Record<string, string> = {
  task: "bg-blue-50 text-blue-700 border-blue-200",
  project: "bg-purple-50 text-purple-700 border-purple-200",
  note: "bg-gray-50 text-gray-700 border-gray-200",
  event: "bg-blue-50 text-blue-700 border-blue-200",
  decision: "bg-orange-50 text-orange-700 border-orange-200",
  reminder: "bg-amber-50 text-amber-700 border-amber-200",
};

/**
 * Once the analysis job is done, fetch the user's most recent analyzed
 * inbox item and surface it as a tangible preview of "what the IA just did
 * for you" before the validation screen. We poll briefly because the
 * inbox row is written by the same worker that flipped the job status to
 * `done` — depending on scheduling there may be a 1–2 s gap before the
 * row is visible to GET /inbox.
 *
 * Falls back silently to nothing if no analyzed item shows up within ~10 s
 * (e.g. analysis finished without producing any actionable mail). This
 * keeps the screen useful even on edge cases instead of blocking with a
 * stale spinner.
 */
function FirstAnalyzedMailPreview() {
  const startedAtRef = useRef<number>(Date.now());
  const TIMEOUT_MS = 10_000;
  const inbox = useListInbox(
    { status: "analyzed" },
    {
      query: {
        queryKey: getListInboxQueryKey({ status: "analyzed" }),
        // Always bypass cache when this preview mounts: the user just
        // finished an analysis and we want the freshest result, not a
        // stale snapshot from an earlier visit.
        refetchOnMount: "always",
        staleTime: 0,
        // Poll every 1.5 s for the first 10 s after the analysis
        // completes; then stop. We keep polling even when stale
        // analyzed items already exist in cache, because the freshly
        // analyzed mail from the just-completed job may land 1–2 s
        // later — we want the top of the list to reflect *this* run.
        refetchInterval: (query) => {
          if (Date.now() - startedAtRef.current > TIMEOUT_MS) return false;
          const data = query.state.data as InboxItem[] | undefined;
          // Once we have data AND the refetch following mount has
          // completed, one more cycle is enough — stop after the next
          // tick to avoid hammering the API.
          return data && data.length > 0 ? 3000 : 1500;
        },
      },
    },
  );

  const first = inbox.data?.[0];

  if (inbox.isLoading || (!first && Date.now() - startedAtRef.current < TIMEOUT_MS)) {
    return (
      <div
        className="w-full bg-secondary/30 border border-border/40 rounded-xl p-4 text-left text-xs text-muted-foreground flex items-center gap-2"
        data-testid="onboarding-first-mail-loading"
      >
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        Préparation d'un aperçu de votre premier e-mail analysé…
      </div>
    );
  }

  if (!first) {
    // Nothing real to show — fall back to a generic illustrative card so
    // the user still gets a concrete idea of what an analyzed mail looks
    // like inside OneNext.
    return <SampleMailPreview />;
  }

  const body = first.rawText ?? first.transcriptText ?? "";
  const bodyPreview = body.length > 220 ? `${body.slice(0, 220).trim()}…` : body;

  return (
    <div
      className="w-full bg-card border border-border rounded-xl p-4 text-left space-y-3"
      data-testid="onboarding-first-mail-preview"
    >
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        <Mail className="w-3.5 h-3.5 text-primary" />
        Aperçu de votre premier e-mail analysé
      </div>
      {bodyPreview && (
        <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap line-clamp-4">
          {bodyPreview}
        </p>
      )}
      {first.aiSummary && (
        <div className="bg-secondary/30 rounded-lg p-3 text-sm text-secondary-foreground border border-secondary">
          <div className="flex items-center gap-1.5 mb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            <Sparkles className="w-3 h-3" /> Résumé IA
          </div>
          {first.aiSummary}
        </div>
      )}
    </div>
  );
}

/**
 * Generic illustrative mail card used as a graceful fallback when the
 * server didn't return any real analyzed item within the polling window
 * (e.g. very small mailbox, all mails skipped by the triage rules, fresh
 * account with no incoming mail yet). Clearly labelled as an example so
 * the user is never misled into thinking it's their own data.
 */
function SampleMailPreview() {
  return (
    <div
      className="w-full bg-card border border-border rounded-xl p-4 text-left space-y-3"
      data-testid="onboarding-sample-mail-preview"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          <Mail className="w-3.5 h-3.5 text-primary" />
          Exemple : à quoi ressemble une analyse
        </div>
        <Badge variant="outline" className="text-[10px] py-0 h-5 font-normal">
          Exemple
        </Badge>
      </div>
      <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap line-clamp-4">
        Bonjour,{"\n"}Peux-tu me confirmer la date de la réunion de relecture
        du contrat avant vendredi ? Je dois caler les disponibilités de
        l'équipe juridique.{"\n"}Merci !
      </p>
      <div className="bg-secondary/30 rounded-lg p-3 text-sm text-secondary-foreground border border-secondary">
        <div className="flex items-center gap-1.5 mb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          <Sparkles className="w-3 h-3" /> Résumé IA
        </div>
        Demande de confirmation pour la réunion de relecture du contrat
        avant vendredi — réponse attendue côté juridique.
      </div>
    </div>
  );
}

function Step4Validate({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const list = useListSuggestions({ status: "pending" });
  const accept = useAcceptSuggestion();
  const reject = useRejectSuggestion();
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [committing, setCommitting] = useState(false);

  const suggestions = useMemo<EnrichedSuggestion[]>(
    () => list.data?.suggestions ?? [],
    [list.data?.suggestions],
  );

  // Default: every suggestion is checked (i.e. will be created).
  useEffect(() => {
    if (suggestions.length === 0) return;
    setChecked((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const s of suggestions) {
        if (next[s.id] === undefined) {
          next[s.id] = true;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [suggestions]);

  const checkedCount = useMemo(
    () => suggestions.filter((s) => checked[s.id]).length,
    [suggestions, checked],
  );

  const finishAndRedirect = () => {
    queryClient.invalidateQueries({ queryKey: getGetTodayQueryKey() });
    queryClient.invalidateQueries({ queryKey: getListProjectsQueryKey() });
    queryClient.invalidateQueries({
      queryKey: getListSuggestionsQueryKey({ status: "pending" }),
    });
    onDone();
  };

  const commit = async () => {
    if (suggestions.length === 0) {
      finishAndRedirect();
      return;
    }
    setCommitting(true);
    let accepted = 0;
    let rejected = 0;
    let failed = 0;
    for (const s of suggestions) {
      try {
        if (checked[s.id]) {
          await accept.mutateAsync({ id: s.id });
          accepted++;
        } else {
          await reject.mutateAsync({ id: s.id });
          rejected++;
        }
      } catch {
        failed++;
      }
    }
    setCommitting(false);
    if (failed > 0) {
      // Don't mark onboarding as complete and don't navigate away — keep the
      // user on the validation step so they can review what's left and retry.
      // Refresh the suggestions list so the items that succeeded disappear.
      queryClient.invalidateQueries({
        queryKey: getListSuggestionsQueryKey({ status: "pending" }),
      });
      toast({
        title: t("onboarding_page.validate_partial_title"),
        description: t("onboarding_page.validate_partial_desc", { accepted, rejected, failed }),
        variant: "destructive",
      });
      return;
    }
    toast({
      title: t("onboarding_page.validate_done_title"),
      description: t("onboarding_page.validate_done_desc", { accepted, rejected }),
    });
    finishAndRedirect();
  };

  if (list.isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
        <Loader2 className="w-6 h-6 animate-spin mb-3" />
        <p className="text-sm">{t("onboarding_page.loading_suggestions")}</p>
      </div>
    );
  }

  if (suggestions.length === 0) {
    return (
      <div className="flex flex-col items-center text-center space-y-6 max-w-lg mx-auto">
        <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center">
          <CheckCircle2 className="w-6 h-6 text-primary" />
        </div>
        <div className="space-y-2">
          <h2 className="text-2xl font-bold">{t("onboarding_page.all_up_to_date_title")}</h2>
          <p className="text-muted-foreground">
            {t("onboarding_page.all_up_to_date_desc")}
          </p>
        </div>
        <Button size="lg" className="w-full rounded-xl" onClick={finishAndRedirect}>
          {t("onboarding_page.go_to_today_btn")} <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    );
  }

  return (
    <div className="w-full max-w-2xl mx-auto space-y-6">
      <div className="text-center space-y-2">
        <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto">
          <ListChecks className="w-6 h-6 text-primary" />
        </div>
        <h2 className="text-2xl font-bold">{t("onboarding_page.validate_suggestions_title")}</h2>
        <p className="text-muted-foreground">
          {t("onboarding_page.validate_suggestions_desc", { count: suggestions.length })}
        </p>
      </div>

      <div className="flex items-center justify-between text-sm bg-secondary/30 rounded-lg px-4 py-2 border border-border/40">
        <button
          type="button"
          onClick={() => {
            const all: Record<string, boolean> = {};
            for (const s of suggestions) all[s.id] = true;
            setChecked(all);
          }}
          className="text-primary hover:underline font-medium"
        >
          {t("onboarding_page.check_all_btn")}
        </button>
        <span className="text-muted-foreground">
          {t("onboarding_page.checked_count", { checked: checkedCount, total: suggestions.length })}
        </span>
        <button
          type="button"
          onClick={() => {
            const none: Record<string, boolean> = {};
            for (const s of suggestions) none[s.id] = false;
            setChecked(none);
          }}
          className="text-muted-foreground hover:underline font-medium"
        >
          {t("onboarding_page.uncheck_all_btn")}
        </button>
      </div>

      <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1">
        {suggestions.map((s) => {
          const payload = (s.payload ?? {}) as {
            title?: string;
            description?: string;
            project_hint?: string;
            due_date?: string;
          };
          const typeColor = TYPE_COLORS[s.type] ?? "bg-gray-50 text-gray-700 border-gray-200";
          const typeLabel = t(`onboarding_page.type_${s.type}`, { defaultValue: s.type });
          const isChecked = checked[s.id] ?? true;
          return (
            <label
              key={s.id}
              className={`flex items-start gap-3 rounded-xl p-3 border cursor-pointer transition-colors ${
                isChecked
                  ? "bg-card border-border"
                  : "bg-muted/30 border-border/40 opacity-60"
              }`}
            >
              <Checkbox
                checked={isChecked}
                onCheckedChange={(v) =>
                  setChecked((prev) => ({ ...prev, [s.id]: v === true }))
                }
                className="mt-0.5"
              />
              <div className="flex-1 min-w-0 space-y-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge
                    variant="outline"
                    className={`text-[10px] px-1.5 py-0 ${typeColor}`}
                  >
                    {typeLabel}
                  </Badge>
                  {s.matchedProject && (
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                      {s.matchedProject.name}
                    </Badge>
                  )}
                  {!s.matchedProject && payload.project_hint && (
                    <Badge
                      variant="outline"
                      className="text-[10px] px-1.5 py-0 italic"
                    >
                      ~ {payload.project_hint}
                    </Badge>
                  )}
                </div>
                <p className="text-sm font-medium leading-snug">
                  {payload.title ?? t("onboarding_page.untitled")}
                </p>
                {payload.description && (
                  <p className="text-xs text-muted-foreground line-clamp-2">
                    {payload.description}
                  </p>
                )}
                {s.inboxSource && (
                  <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
                    <InboxIcon className="w-3 h-3" />
                    <span className="truncate">
                      {s.inboxSource.rawText?.slice(0, 80) ??
                        s.inboxSource.transcriptText?.slice(0, 80) ??
                        "Capture"}
                    </span>
                  </div>
                )}
              </div>
            </label>
          );
        })}
      </div>

      <div className="flex flex-col gap-2 pt-2">
        <Button
          size="lg"
          className="w-full rounded-xl"
          onClick={commit}
          disabled={committing}
        >
          {committing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          {t("onboarding_page.create_items_btn", { count: checkedCount })} <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}

export default function OnboardingPage() {
  const { t } = useTranslation();
  const [step, setStepState] = useState(0);
  const [, setLocation] = useLocation();
  const search = useSearch();
  const { user, isLoaded } = useUser();
  const mailAccounts = useListMailAccounts();
  const analyzeNow = useAssistantAnalyzeNow();

  // Wrap setStep so every transition is mirrored to localStorage. A refresh
  // (or accidental tab reload during the long analysis) then resumes on the
  // same step instead of dropping back to step 1.
  const setStep = (next: number) => {
    writePersistedStep(user?.id, next);
    setStepState(next);
  };

  // Deep-link support: /onboarding?step=2 jumps straight to first-analysis,
  // used by the "first-analysis pending" banner on /app/today so users who
  // already completed the basic onboarding can resume the analysis later.
  useEffect(() => {
    const params = new URLSearchParams(search);
    const requested = Number(params.get("step"));
    if (Number.isFinite(requested) && requested >= 0 && requested <= 1) {
      setStepState(requested);
      writePersistedStep(user?.id, requested);
    }
  }, [search, user?.id]);

  // Restore the persisted step on mount once Clerk has resolved the user
  // (key is namespaced by user id). Only runs when no explicit ?step= is
  // present in the URL — explicit deep-links win.
  useEffect(() => {
    if (!isLoaded) return;
    const params = new URLSearchParams(search);
    if (params.has("step")) return;
    const persisted = readPersistedStep(user?.id);
    if (persisted > 0) setStepState(persisted);
  }, [isLoaded, user?.id, search]);

  useEffect(() => {
    if (!isLoaded) return;
    const params = new URLSearchParams(search);
    if (params.has("step")) return; // explicit deep-link wins
    const key = onboardingDoneKey(user?.id);
    const analysisKey = firstAnalysisDoneKey(user?.id);
    if (
      localStorage.getItem(key) === "true" &&
      localStorage.getItem(analysisKey) === "true"
    ) {
      setLocation("/app/today");
    }
  }, [isLoaded, user?.id, setLocation, search]);

  // ── Returning-user fast-path (cross-device) ───────────────────────────────
  // Clerk's user.createdAt is the authoritative signal: if the account is
  // older than ONBOARDING_WINDOW_MS, this is a reconnection (new device,
  // cleared storage, OAuth re-login…), not a genuine first-time sign-up.
  // We skip the onboarding welcome entirely and go straight to the app.
  // An explicit ?step= deep-link always wins so the analysis-resume banner
  // on /app/today can still bring users back here for the first-analysis.
  useEffect(() => {
    if (!isLoaded || !user?.createdAt) return;
    const params = new URLSearchParams(search);
    if (params.has("step")) return;
    const accountAgeMs = Date.now() - user.createdAt.getTime();
    if (accountAgeMs < ONBOARDING_WINDOW_MS) return; // genuinely new user
    // Persist localStorage flags so same-device future visits skip this too.
    const key = onboardingDoneKey(user.id);
    const analysisKey = firstAnalysisDoneKey(user.id);
    if (localStorage.getItem(key) !== "true") localStorage.setItem(key, "true");
    if (localStorage.getItem(analysisKey) !== "true")
      localStorage.setItem(analysisKey, "true");
    localStorage.removeItem(onboardingStepKey(user.id));
    setLocation("/app/today");
  }, [isLoaded, user?.id, user?.createdAt, setLocation, search]);

  // Cross-device / cleared-storage rescue: if the server already knows about
  // a mail account for this user *at the moment the page loads*, we can
  // safely treat the basic onboarding as complete (they've clearly been
  // here before) and send them straight to /app/today instead of re-asking
  // them to connect a mailbox.
  //
  // One-shot semantics on purpose: we only trigger on the very first
  // successful mail-accounts response after mount. Otherwise, any
  // background refetch that happens while the user is on steps 2/3/4
  // (e.g. right after they finish connecting their first mailbox) would
  // also fire this redirect and skip them past the first-analysis +
  // validation steps. We intentionally do NOT touch the first-analysis
  // flag — the "analysis pending" banner on /today handles that case
  // independently.
  const rescueRedirectFiredRef = useRef(false);
  useEffect(() => {
    if (rescueRedirectFiredRef.current) return;
    if (!isLoaded) return;
    if (!mailAccounts.isSuccess) return;
    const params = new URLSearchParams(search);
    if (params.has("step")) return; // explicit deep-link wins
    // Only act if the user is still on the welcome screen and has no
    // in-flight onboarding state. This protects users who genuinely want
    // to revisit onboarding even though they have an account.
    if (readPersistedStep(user?.id) > 0) {
      rescueRedirectFiredRef.current = true;
      return;
    }
    const accounts = mailAccounts.data ?? [];
    if (accounts.length === 0) {
      // No accounts on first load → nothing to rescue. Latch so we don't
      // re-evaluate later if they connect one mid-flow.
      rescueRedirectFiredRef.current = true;
      return;
    }
    rescueRedirectFiredRef.current = true;
    const key = onboardingDoneKey(user?.id);
    if (localStorage.getItem(key) !== "true") {
      localStorage.setItem(key, "true");
    }
    localStorage.removeItem(onboardingStepKey(user?.id));
    setLocation("/app/today");
  }, [
    isLoaded,
    mailAccounts.isSuccess,
    mailAccounts.data,
    user?.id,
    search,
    setLocation,
  ]);

  function markIntroDone() {
    const key = onboardingDoneKey(user?.id);
    localStorage.setItem(key, "true");
  }

  function markAllDoneAndRedirect() {
    markIntroDone();
    const analysisKey = firstAnalysisDoneKey(user?.id);
    localStorage.setItem(analysisKey, "true");
    // Onboarding complete — clear the persisted step so a future visit
    // (e.g. via the deep-link banner) starts from a clean state.
    localStorage.removeItem(onboardingStepKey(user?.id));
    setLocation("/app/today");
  }

  function skipMailAndGoToApp() {
    markIntroDone();
    const analysisKey = firstAnalysisDoneKey(user?.id);
    localStorage.setItem(analysisKey, "true");
    const skipKey = user?.id
      ? `onenext_onboarding_skipped_mail:${user.id}`
      : "onenext_onboarding_skipped_mail";
    localStorage.setItem(skipKey, "true");
    localStorage.removeItem(onboardingStepKey(user?.id));
    setLocation("/app/today");
  }

  function skipToToday() {
    markIntroDone();
    localStorage.removeItem(onboardingStepKey(user?.id));
    setLocation("/app/today");
  }

  function finishAndGoToApp() {
    // Fire background sync with sensible defaults — no waiting, user goes
    // straight to the app. The job id is persisted so the shell can show
    // a discreet progress indicator.
    analyzeNow.mutate(
      { data: { sinceDays: 90, unlimited: true, depth: "3m", maxMails: 500 } },
      {
        onSuccess: (resp) => {
          if (resp.jobId) writePersistedAnalysisJob(user?.id, resp.jobId);
        },
      },
    );
    markAllDoneAndRedirect();
  }

  const hasMailAccount = (mailAccounts.data?.length ?? 0) > 0;

  const STEPS = [
    <Step1Promise
      key="promise"
      onNext={() => hasMailAccount ? finishAndGoToApp() : setStep(1)}
      onSkip={skipToToday}
    />,
    <Step2Mail
      key="mail"
      onNext={finishAndGoToApp}
      onSkip={finishAndGoToApp}
      onSkipMail={skipMailAndGoToApp}
    />,
  ];

  return (
    <div className="min-h-screen bg-background flex flex-col font-sans">
      <header className="flex items-center justify-between px-6 py-4 border-b border-border/40">
        <div className="flex items-center gap-2">
          <Logo size={28} />
          <span className="font-bold text-foreground">OneNext</span>
        </div>
        <div className="flex items-center gap-4">
          <StepIndicator current={step} total={STEPS.length} />
          <button
            onClick={skipToToday}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
            title={t("onboarding_page.skip_onboarding_title")}
          >
            <X className="w-3.5 h-3.5" />
            {t("onboarding_page.skip_btn")}
          </button>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-12 overflow-y-auto">
        <div className="w-full">{STEPS[step]}</div>
      </main>
    </div>
  );
}
